import { Box, Button, useTheme, Stack } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SubmissionForm } from "components/Submission/SubmissionForm";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY } from "queries/submissions";
import { SubmissionHeaderNavigation } from "components/Submission/SubmissionHeaderNavigation";
import { getSavedWasteSubmissionsUrl } from "../navigation";

export function CreateEditSubmission() {
  const { tenantId, submissionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { isStaff } = useCurrentUser();

  const isEditMode = Boolean(submissionId);

  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId ?? "");

  // Extract and log taskId and startDate parameters
  const taskId = searchParams.get("taskId");

  // Expect format YYYY-MM-DD
  const startDate = searchParams.get("startDate");

  // Query the virtual event data
  const { data: virtualEventData } = useQuery(
    GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY,
    {
      variables: {
        taskId: taskId || "",
        deadline: startDate || "",
      },
      skip: !taskId || !startDate || isEditMode,
      fetchPolicy: "network-only",
    }
  );

  // Prepare form data from virtual event - memoized to prevent unnecessary form resets
  const formData = useMemo(() => {
    return virtualEventData?.virtualEvent
      ? {
          title: virtualEventData.virtualEvent.title,
          facilityIds:
            virtualEventData.virtualEvent.facilities?.map((f) => f.id) || [],
          dueDate: startDate,
          submissionType:
            virtualEventData.virtualEvent.submission?.submissionType,
          notes: virtualEventData.virtualEvent.submission?.notes ?? undefined,
          recipient:
            virtualEventData.virtualEvent.submission?.recipient ?? undefined,
          submissionMethod:
            virtualEventData.virtualEvent.submission?.submissionMethod,
        }
      : null;
  }, [virtualEventData?.virtualEvent, startDate]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: isEditMode ? "Edit Submission" : "Create Submission" },
  ]);

  const handleSubmissionSaved = () => {
    // Navigate back to the submissions list, preserving filters
    navigate(submissionsUrl);
  };

  const handleCancel = () => {
    navigate(submissionsUrl);
  };

  return (
    <Stack>
      <SubmissionHeaderNavigation
        tenantId={tenantId ?? ""}
        actions={
          isStaff && isEditMode ? (
            <Button
              component="a"
              href={`/staff/fulfillment/waste/submissions/${submissionId}`}
              target="_blank"
              variant="outlined"
              color="warning"
              endIcon={<OpenInNewIcon />}
            >
              Open Fulfillment
            </Button>
          ) : undefined
        }
      />

      {/* Main Content Area */}
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.grey50.light
              : theme.palette.grey50.dark,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Main content area without padding to allow full-width sections */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <SubmissionForm
            onClose={handleCancel}
            onSubmit={handleSubmissionSaved}
            submissionId={submissionId}
            isEditMode={isEditMode}
            virtualTaskId={
              taskId && startDate ? `${taskId}_${startDate}` : undefined
            }
            initialFormData={formData || undefined}
          />
        </Box>
      </Box>
    </Stack>
  );
}
